import {
  ITeamResponseSchema,
  IUser,
  UserStatusEnum,
  UserTypeEnum,
} from '@monetize/types/app';
import { useMemo, useState } from 'react';
import { handleApiErrorToast } from '../../../../../api/axios';
import { MBox } from '../../../../../components/Monetize';
import { UserAvatarAndName } from '../../../../../components/Monetize/MCustomSelect/components/UserAvatarAndName';
import { MCustomMultiSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import { useAuth } from '../../../../../services/auth0';

const USER_SEARCH_PARAMS = {
  status: { in: [UserStatusEnum.ACTIVE, UserStatusEnum.INVITED] },
  type: { in: [UserTypeEnum.USER] },
};

const NO_SELECTION: IUser[] = [];

interface AddMembersInlineProps {
  team: ITeamResponseSchema;
  onAdd: (userIds: string[]) => Promise<void>;
  isDisabled: boolean;
}

export const AddMembersInline = ({
  team,
  onAdd,
  isDisabled,
}: AddMembersInlineProps) => {
  const { tenantId } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Existing members are dropped from the results rather than shown disabled:
  // the only reason a searched user is un-addable is that they are already on
  // the team, which the members table right below already makes obvious.
  // This must be `filterItems`, not `transformDataFromApi` - the latter runs
  // inside the query function, so the narrowed list is what gets cached under
  // a key of endpoint + search params: it would not update when someone is
  // added, and it would be served to every other select on this endpoint.
  const memberIds = useMemo(
    () => new Set(team.users.map(({ id }) => id)),
    [team.users],
  );
  const excludeExistingMembers = useMemo(
    () => (users: IUser[]) =>
      (users || []).filter(({ id }) => !memberIds.has(id)),
    [memberIds],
  );

  /**
   * Apply adds straight away. The selection is handed in by the select rather
   * than read from state: `onMultiSelectSubmit` and the close handler fire in
   * the same tick, so state set by the former is not yet visible here.
   */
  const handleApply = async (members: IUser[]) => {
    if (!members.length) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onAdd(members.map(({ id }) => id ?? ''));
    } catch (error) {
      handleApiErrorToast(error);
    } finally {
      // Flipping this back closes the popover; see `applyPendingRef` in
      // MCustomSelect.
      setIsSubmitting(false);
    }
  };

  return (
    <MBox maxW="480px" data-testid="add-members">
      <MCustomMultiSelect
        name="add-members"
        inputTestId="add-members-select"
        placeholder="Add Members"
        returnItem
        itemValue="id"
        itemTitle="name"
        showQueryInput
        isSelectOpen={false}
        loadAll
        endpoint={`/api/tenants/${tenantId}/users`}
        additionalSearchParams={USER_SEARCH_PARAMS}
        filterItems={excludeExistingMembers}
        value={NO_SELECTION}
        isDisabled={isDisabled || isSubmitting}
        renderItemContent={({ title }) => (
          <UserAvatarAndName userName={title} textProps={{ fontSize: 'sm' }} />
        )}
        onChange={handleApply}
        isCloseButtonLoading={isSubmitting}
        showCheckCount
        closeButtonText="Apply"
      />
    </MBox>
  );
};
